import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Bedrock's type steps and radius scale, taught to tailwind-merge.
 *
 * This is not tuning — without it `cn` silently DROPS classes, and the drop is
 * invisible in review. tailwind-merge resolves conflicts by classifying each
 * utility into a group, and its `font-size` group is a fixed list of Tailwind's
 * own scale (`text-xs` … `text-9xl`). A custom `fontSize` key like `text-h3`
 * is not on that list, so v2 falls through to its `text-color` classifier —
 * which matches any `text-*` — and then `cn('text-h3', 'text-foreground')`
 * looks like two colours in conflict and only the last one survives. The
 * heading renders at body size with no error anywhere.
 *
 * Found the hard way: `SectionCard`'s `h2` lost `text-h3` to the
 * `text-foreground` beside it, and only a test that asserted the class caught
 * it. Every Bedrock step is registered below, so the same silent drop cannot
 * happen to `text-metric` inside a tile or `text-label` inside an eyebrow.
 *
 * `rounded-xs` is registered for the same reason: v2's `borderRadius` group
 * knows `rounded-sm`…`rounded-3xl` but not the `xs` step Bedrock adds for
 * chips, so `cn('rounded-xs', 'rounded-full')` would not resolve.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: [
            'display',
            'metric',
            'metric-sm',
            'h1',
            'h2',
            'h3',
            'body',
            'small',
            'micro',
            'label',
            'code',
          ],
        },
      ],
      'font-weight': [{ font: ['control', 'heading'] }],
      rounded: [{ rounded: ['xs'] }],
    },
  },
});

/**
 * Compose Tailwind class names, deduplicating conflicting utilities. This is
 * the standard Radix-+-Tailwind pattern: clsx for conditional logic,
 * tailwind-merge to resolve "p-2 p-4" → "p-4".
 *
 * Used by every component variant via `class-variance-authority` and any
 * consumer that conditionally composes classes.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
